import React, { useCallback, useMemo, useState } from 'react';
import { YearbookPage, PAGE_WIDTH, PAGE_HEIGHT } from '@/types/yearbook';
import ReadOnlyElement from './ReadOnlyElement';

interface OverviewViewProps {
  pages: YearbookPage[];
  currentPageIndex: number;
  onSelectPage: (index: number) => void;
  onSwitchToPage: (index: number) => void;
  onReorderPage?: (fromIndex: number, toIndex: number) => void;
}

const THUMB_SCALE = 0.18;
const THUMB_W = PAGE_WIDTH * THUMB_SCALE;
const THUMB_H = PAGE_HEIGHT * THUMB_SCALE;
const SPREAD_GAP = 2;

interface Spread {
  label: string;
  pages: { page: YearbookPage; index: number }[];
  alignLeft?: boolean;
  isCoverSpread?: boolean;
}

function buildOverviewSpreads(pages: YearbookPage[]): Spread[] {
  const spreads: Spread[] = [];

  // Covers spread: [back_cover (4ème), front_cover (1ère)]
  const coverItems = pages
    .map((p, i) => ({ page: p, index: i }))
    .filter((item) => !!item.page.coverType);

  if (coverItems.length > 0) {
    // back cover on left, front cover on right (order from array: [back, front])
    spreads.push({ label: '4ème · 1ère de couverture', pages: coverItems, isCoverSpread: true });
  }

  const interior = pages
    .map((p, i) => ({ page: p, index: i }))
    .filter((item) => !item.page.coverType);

  if (interior.length === 0) return spreads;

  // First page alone, positioned LEFT
  spreads.push({ label: `Page ${interior[0].page.pageNumber}`, pages: [interior[0]], alignLeft: false });

  for (let i = 1; i < interior.length - 1; i += 2) {
    const left = interior[i];
    const right = interior[i + 1];
    if (right) {
      spreads.push({ label: `${left.page.pageNumber}–${right.page.pageNumber}`, pages: [left, right] });
    } else {
      spreads.push({ label: `Page ${left.page.pageNumber}`, pages: [left] });
    }
  }

  // Last page alone (if not already paired), positioned LEFT
  if (interior.length > 1 && (interior.length - 1) % 2 !== 0) {
    const last = interior[interior.length - 1];
    spreads.push({ label: `Page ${last.page.pageNumber}`, pages: [last], alignLeft: true });
  }

  return spreads;
}

const PageThumb: React.FC<{
  page: YearbookPage;
  index: number;
  isActive: boolean;
  isCover: boolean;
  dropIndicator: 'left' | 'right' | null;
  onSelect: (i: number) => void;
  onDoubleClick: (i: number) => void;
  onDragStart: (i: number) => void;
  onDragOver: (e: React.DragEvent, i: number) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, i: number) => void;
}> = ({ page, index, isActive, isCover, dropIndicator, onSelect, onDoubleClick, onDragStart, onDragOver, onDragLeave, onDrop }) => {
  const BG_BLEED = 14.1732;
  const bgIsImage = page.background.type !== 'color';
  const hasUserBg = bgIsImage && (page.background.trame_background_id != null || page.background.trame_background_custom_id != null);
  const bgInset = hasUserBg ? BG_BLEED : 0;
  const pageW = page.pageWidth ?? PAGE_WIDTH;
  const thumbW = pageW * THUMB_SCALE;
  const bgStyle: React.CSSProperties = bgIsImage
    ? {}
    : { backgroundColor: page.background.value };

  return (
    <div
      className={`relative overflow-visible ${isCover ? '' : 'cursor-grab active:cursor-grabbing'}`}
      style={{ width: thumbW, height: THUMB_H }}
      draggable={!isCover}
      onDragStart={(e) => {
        if (isCover) { e.preventDefault(); return; }
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(index));
        onDragStart(index);
      }}
      onDragOver={(e) => onDragOver(e, index)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, index)}
      onClick={() => onSelect(index)}
      onDoubleClick={() => onDoubleClick(index)}
    >
      {/* Drop indicator left */}
      {dropIndicator === 'left' && (
        <div className="absolute -left-1.5 top-0 bottom-0 w-1 bg-editor-sidebar-active rounded-full z-10" />
      )}
      {/* Drop indicator right */}
      {dropIndicator === 'right' && (
        <div className="absolute -right-1.5 top-0 bottom-0 w-1 bg-editor-sidebar-active rounded-full z-10" />
      )}
      <div
        className={`relative w-full h-full overflow-hidden transition-all ${
          isActive ? 'ring-2 ring-editor-sidebar-active ring-offset-1 ring-offset-editor-canvas' : 'hover:ring-1 hover:ring-editor-element-hover'
        }`}
      >
        {bgIsImage && (
          <img
            src={page.background.value}
            alt=""
            className="absolute pointer-events-none"
            style={{
              top: -bgInset * THUMB_SCALE,
              left: -bgInset * THUMB_SCALE,
              width: (pageW + bgInset * 2) * THUMB_SCALE,
              height: (PAGE_HEIGHT + bgInset * 2) * THUMB_SCALE,
              maxWidth: 'none',
              objectFit: 'cover',
            }}
            draggable={false}
          />
        )}
        <div
          className="origin-top-left"
          style={{ width: pageW, height: PAGE_HEIGHT, transform: `scale(${THUMB_SCALE})`, ...bgStyle }}
        >
          {page.elements.map((el) => (
            <ReadOnlyElement key={el.id} element={el} />
          ))}
        </div>
      </div>
    </div>
  );
};

const OverviewView: React.FC<OverviewViewProps> = ({ pages, currentPageIndex, onSelectPage, onSwitchToPage, onReorderPage }) => {
  const spreads = useMemo(() => buildOverviewSpreads(pages), [pages]);
  const [dragFromIndex, setDragFromIndex] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<{ index: number; side: 'left' | 'right' } | null>(null);

  const handleDoubleClick = useCallback((index: number) => onSwitchToPage(index), [onSwitchToPage]);

  const handleDragOver = useCallback((e: React.DragEvent, pageIndex: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    // Determine left/right based on mouse position within the thumb
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const midX = rect.left + rect.width / 2;
    const side = e.clientX < midX ? 'left' : 'right';
    // Don't show indicator on cover pages
    const page = pages[pageIndex];
    if (page?.coverType) return;
    setDropTarget({ index: pageIndex, side });
  }, [pages]);

  const handleDragLeave = useCallback(() => {
    setDropTarget(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (isNaN(fromIndex) || !onReorderPage) return;

    const targetPage = pages[targetIndex];
    if (targetPage?.coverType) return;

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const midX = rect.left + rect.width / 2;
    const insertBefore = e.clientX < midX;

    const toIndex = insertBefore ? targetIndex : targetIndex + 1;
    onReorderPage(fromIndex, toIndex);

    setDragFromIndex(null);
    setDropTarget(null);
  }, [pages, onReorderPage]);

  const handleDragEnd = useCallback(() => {
    setDragFromIndex(null);
    setDropTarget(null);
  }, []);

  return (
    <div className="flex-1 overflow-auto bg-editor-canvas p-8" onDragEnd={handleDragEnd}>
      {/* Cover spread — full row, no reorder */}
      {spreads.filter((s) => s.isCoverSpread).map((spread, si) => (
        <div key={`cover-${si}`} className="flex flex-col items-center gap-2 mb-8 w-full">
          <div className="flex items-start rounded-sm overflow-visible editor-shadow" style={{ gap: 0 }}>
            {spread.pages.map(({ page, index }) => (
              <div key={page.id} className="flex flex-col items-center gap-1">
                <PageThumb
                  page={page}
                  index={index}
                  isActive={index === currentPageIndex}
                  isCover={true}
                  dropIndicator={null}
                  onSelect={onSelectPage}
                  onDoubleClick={handleDoubleClick}
                  onDragStart={setDragFromIndex}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                />
                {page.coverType !== 'tranche' && (
                  <span className="text-xs text-muted-foreground">
                    {page.coverType === 'back' ? '4ème de couverture' : '1ère de couverture'}
                  </span>
                )}
              </div>
            ))}
          </div>
          <div className="w-full border-b border-border mt-2" />
        </div>
      ))}
      {/* Interior spreads */}
      <div className="flex flex-wrap justify-center gap-6">
        {spreads.filter((s) => !s.isCoverSpread).map((spread, si) => {
          return (
            <div key={si} className="flex flex-col items-center gap-2">
              <div className="flex rounded-sm overflow-visible editor-shadow" style={{ gap: SPREAD_GAP, width: THUMB_W * 2 + SPREAD_GAP }}>
                {/* Spacer on the left for right-aligned single pages */}
                {spread.pages.length === 1 && spread.alignLeft === false && (
                  <div style={{ width: THUMB_W, height: THUMB_H }} />
                )}
                {spread.pages.map(({ page, index }) => (
                  <PageThumb
                    key={page.id}
                    page={page}
                    index={index}
                    isActive={index === currentPageIndex}
                    isCover={!!page.coverType}
                    dropIndicator={dropTarget?.index === index ? dropTarget.side : null}
                    onSelect={onSelectPage}
                    onDoubleClick={handleDoubleClick}
                    onDragStart={setDragFromIndex}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  />
                ))}
                {/* Spacer on the right for left-aligned single pages */}
                {spread.pages.length === 1 && spread.alignLeft !== false && (
                  <div style={{ width: THUMB_W, height: THUMB_H }} />
                )}
              </div>
              <span className="text-xs font-medium text-muted-foreground">{spread.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OverviewView;
